import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { startCheckout } from "@/lib/razorpay";
import { renderPayPalButton } from "@/lib/paypal";

const PP_CONTAINER_ID = "paypal-btn-container";

export const BuyModal = ({ product, onClose, brandName }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [country, setCountry] = useState("IN");
  const [method, setMethod] = useState("razorpay");
  const [busy, setBusy] = useState(false);
  const [paypalRendered, setPaypalRendered] = useState(false);

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email.";
    if (!form.phone || !isValidPhoneNumber(form.phone)) return "Please enter a valid phone number for the selected country.";
    return null;
  };

  const payRazorpay = async (e) => {
    e && e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    await startCheckout({ product, buyer: form, brandName, onSuccess: () => onClose() });
    setBusy(false);
  };

  const showPayPal = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setMethod("paypal");
    setPaypalRendered(true);
    setTimeout(() => {
      renderPayPalButton({
        containerId: PP_CONTAINER_ID,
        product,
        buyer: { ...form, country },
        onSuccess: () => onClose(),
      });
    }, 50);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-midnight/90 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto" data-testid="buy-modal">
      <form onSubmit={payRazorpay} className="glass-strong p-8 w-full max-w-md relative my-8">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-ivory/60 hover:text-gold">
          <X className="h-5 w-5" />
        </button>
        <div className="eyebrow mb-2">Checkout</div>
        <h3 className="font-display text-2xl text-ivory">{product.name}</h3>
        <div className="mt-1 text-gold">
          ₹{product.priceInr || 100} <span className="text-ivory/40 text-xs">· or</span> ${product.priceUsd || 2}
        </div>

        <div className="mt-6 grid gap-4">
          {[["name", "Your Name", "text"], ["email", "Email", "email"]].map(([k, label, type]) => (
            <div key={k} className="grid gap-1.5">
              <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">{label}</label>
              <input
                data-testid={`buy-${k}`}
                type={type}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="bg-midnight/70 border border-gold/20 px-3 py-2 text-ivory text-sm focus:outline-none focus:border-gold"
                required
              />
            </div>
          ))}
          <div className="grid gap-1.5">
            <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">Phone</label>
            <div className="bg-midnight/70 border border-gold/20 px-3 py-2 focus-within:border-gold">
              <PhoneInput
                international
                defaultCountry="IN"
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v || "" })}
                onCountryChange={(c) => c && setCountry(c)}
                className="rpi"
              />
            </div>
          </div>

          <div className="grid gap-2 mt-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">Choose payment method</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setMethod("razorpay"); setPaypalRendered(false); }}
                className={`border px-3 py-3 text-[11px] uppercase tracking-[0.2em] transition ${method === "razorpay" ? "border-gold bg-gold/10 text-gold" : "border-gold/25 text-ivory/70 hover:text-gold"}`}
                data-testid="method-razorpay"
              >
                Razorpay · ₹{product.priceInr || 100}
              </button>
              <button
                type="button"
                onClick={showPayPal}
                className={`border px-3 py-3 text-[11px] uppercase tracking-[0.2em] transition ${method === "paypal" ? "border-gold bg-gold/10 text-gold" : "border-gold/25 text-ivory/70 hover:text-gold"}`}
                data-testid="method-paypal"
              >
                PayPal · ${product.priceUsd || 2}
              </button>
            </div>
            <p className="text-[10px] text-ivory/40 leading-relaxed">
              India → Razorpay (INR) · International → PayPal (USD). Choose whichever you prefer.
            </p>
          </div>

          {method === "razorpay" && (
            <button type="submit" disabled={busy} data-testid="buy-pay-razorpay" className="btn-gold mt-2 justify-center disabled:opacity-50">
              {busy ? "Opening payment…" : `Pay ₹${product.priceInr || 100} via Razorpay`}
            </button>
          )}

          {method === "paypal" && (
            <div className="mt-2">
              <div id={PP_CONTAINER_ID} data-testid="paypal-button-container" />
              {!paypalRendered && <div className="text-xs text-ivory/50">Loading PayPal…</div>}
            </div>
          )}

          <p className="text-[11px] text-ivory/45 leading-relaxed">
            Secure payment. After payment, we'll reach out on WhatsApp within 24 hours to begin your journey.
          </p>
        </div>
      </form>
    </div>
  );
};

export default BuyModal;
